/**
 * v2 question dictionary — canonical text per
 * cadence-advisers/website/drafts/specs/tempo-v2-questions-spec.md
 * (with amendments §§ tempo-v2-question-set-amendment-01.md applied).
 *
 * Used by the admin Review page's "Operator Responses" tab to render
 * the operator's raw answers with full question + option text rather
 * than just letter codes.
 *
 * Source of truth at the widget mount lives at
 * cadence-advisers/website/production/assets/tempo-widget-v2.js;
 * this file mirrors it. If the widget changes, update both.
 */

export type RadioOption = { value: string; lead?: string; desc?: string; emph?: string; light?: string };
export type CheckboxOption = { value: string; text: string };

export type QuestionDef =
  | { kind: 'radio'; id: string; phase: Phase; label: string; question: string; helper?: string; options: RadioOption[] }
  | { kind: 'checkbox'; id: string; phase: Phase; label: string; question: string; helper?: string; options: CheckboxOption[]; multiSelect: true; maxSelections?: number }
  | { kind: 'textarea'; id: string; phase: Phase; label: string; question: string; helper?: string; placeholder?: string; optional?: boolean };

export type Phase =
  | 'intake'
  | 'qualifier'
  | 'context'
  | 'pathA'
  | 'pathB'
  | 'convergence';

export const PHASE_LABEL: Record<Phase, string> = {
  intake: 'Intake',
  qualifier: 'Operating Reality',
  context: 'Industry Context',
  pathA: 'Path A — Founder-Led',
  pathB: 'Path B — Management-Led',
  convergence: 'Convergence',
};

export const QUESTIONS: QuestionDef[] = [
  // ── Qualifier ─────────────────────────────────────────────────────
  {
    kind: 'radio', id: 'q1_qualifier', phase: 'qualifier', label: 'Operating reality',
    question: 'Which most accurately describes how your business actually runs today?',
    helper: 'Pick the one closest to your reality, not where you wish you were.',
    options: [
      { value: 'A', lead: 'Lean & founder-driven.', desc: 'Under 15. Founder in the weeds daily — approving quotes, handling escalations.' },
      { value: 'B', lead: 'Departmentalized & management-led.', desc: '16+ people, dedicated departments, middle management. Founder works on the business.' },
      { value: 'C', lead: 'Hybrid: bench gap.', desc: '16+ people but the founder still functionally runs 2+ departments — the bench isn\'t there yet.' },
      { value: 'D', lead: 'Lean but self-managed.', desc: 'Under 15, but the founder has stepped out — the team self-manages day-to-day.' },
    ],
  },

  // ── Industry ──────────────────────────────────────────────────────
  {
    kind: 'radio', id: 'q2_industry', phase: 'context', label: 'Industry context',
    question: 'Which best describes your industry?',
    helper: 'For synthesis context. Doesn\'t change which questions you see.',
    options: [
      { value: 'professional_services', emph: 'Professional services', light: 'consulting, legal, accounting, agency' },
      { value: 'real_estate', emph: 'Real estate', light: 'brokerage, property' },
      { value: 'health_wellness', emph: 'Health & wellness', light: 'salons, spas, fitness, clinics' },
      { value: 'ecommerce_retail', emph: 'E-commerce / retail / DTC' },
      { value: 'trades_manufacturing', emph: 'Trades / light manufacturing / construction' },
      { value: 'saas_software', emph: 'SaaS / software / tech product' },
      { value: 'other', emph: 'Other' },
    ],
  },

  // ── Path A ────────────────────────────────────────────────────────
  { kind: 'radio', id: 'a1_handoff', phase: 'pathA', label: 'Path A · 1 of 17',
    question: 'Right after a new client signs a contract, what actually happens next?',
    options: [
      { value: 'A', lead: 'It\'s on me.', desc: 'I personally email them, set up their folder, add them to a spreadsheet, and ping the team. It\'s mostly in my head.' },
      { value: 'B', lead: 'Templated but manual.', desc: 'We have a checklist or template that someone copies and works through by hand.' },
      { value: 'C', lead: 'Partly automated.', desc: 'A signed contract triggers some automated steps (a Zap, a folder), but a human stitches the rest.' },
      { value: 'D', lead: 'Zero-touch.', desc: 'Signature → onboarding sequence, project setup, billing, kickoff scheduling all fire automatically.' },
    ] },
  { kind: 'radio', id: 'a2_two_weeks', phase: 'pathA', label: 'Path A · 2 of 17',
    question: 'If you had to step out for two weeks tomorrow, with no phone, what breaks first?',
    options: [
      { value: 'A', lead: 'Nearly everything.', desc: 'Sales would stall, payroll questions would pile up, clients would get ghosted.' },
      { value: 'B', lead: 'Sales freezes.', desc: 'Pipeline and pricing decisions would freeze, but ops would keep running for a while.' },
      { value: 'C', lead: 'It limps.', desc: 'Most things would limp along; a few key approvals or client relationships would suffer.' },
      { value: 'D', lead: 'Nothing breaks.', desc: 'The team is documented, empowered, and tooled to operate without me.' },
    ] },
  { kind: 'radio', id: 'a3_data_lives', phase: 'pathA', label: 'Path A · 3 of 17',
    question: 'Where does most of your client and prospect data actually live?',
    options: [
      { value: 'A', lead: 'In my head.', desc: 'In my email inbox, my phone\'s text messages, and my head.' },
      { value: 'B', lead: 'Scattered.', desc: 'Spread across a CRM, spreadsheets, email, and at least one notebook or sticky note.' },
      { value: 'C', lead: 'Mostly in the CRM.', desc: 'Centralized in a CRM, but with known gaps (quotes in PDFs, pipeline updated weekly by hand).' },
      { value: 'D', lead: 'Single source of truth.', desc: 'CRM is enriched automatically and is the system of record for all customer data.' },
    ] },
  { kind: 'radio', id: 'a4_quoting', phase: 'pathA', label: 'Path A · 4 of 17',
    question: 'How do you currently estimate, quote, or price new work?',
    options: [
      { value: 'A', lead: 'Gut feel.', desc: 'I usually quote on a call or in a text and write it down later (sometimes).' },
      { value: 'B', lead: 'Spreadsheet.', desc: 'I have a calculator I built that I open every time.' },
      { value: 'C', lead: 'Templated proposals.', desc: 'PandaDoc, Proposify, etc., but I customize each one heavily.' },
      { value: 'D', lead: 'Configurator.', desc: 'A quote engine generates priced proposals based on inputs — minimal manual touching.' },
    ] },
  { kind: 'radio', id: 'a5_invoicing', phase: 'pathA', label: 'Path A · 5 of 17',
    question: 'How does invoicing and getting paid work?',
    options: [
      { value: 'A', lead: 'Manual chase.', desc: 'I create invoices manually (often late) and chase payment by text or email when I remember.' },
      { value: 'B', lead: 'QuickBooks-by-hand.', desc: 'Invoicing software, but each invoice is created manually and follow-ups are sporadic.' },
      { value: 'C', lead: 'Partly automated.', desc: 'Invoices auto-generate from milestones; reminders are partly automated.' },
      { value: 'D', lead: 'Fully automated.', desc: 'Billing → reminders → dunning → reconciliation, with revenue recognized cleanly.' },
    ] },
  { kind: 'radio', id: 'a6_lead_response', phase: 'pathA', label: 'Path A · 6 of 17',
    question: 'When a new lead comes in, how fast does someone respond?',
    options: [
      { value: 'A', lead: 'Hours, sometimes never.', desc: 'Honestly? Often hours or the next day. Leads sometimes slip entirely.' },
      { value: 'B', lead: 'Same day.', desc: 'Within a few hours during business hours; nights and weekends are hit or miss.' },
      { value: 'C', lead: 'Inside 15 minutes.', desc: 'Within ~15 minutes during business hours; we have an autoresponder after-hours.' },
      { value: 'D', lead: 'Sub-5-min, always on.', desc: 'Sub-5-minute response 24/7 via automated qualification + routing.' },
    ] },
  { kind: 'radio', id: 'a7_handoffs', phase: 'pathA', label: 'Path A · 7 of 17',
    question: 'When work gets handed from you to a team member or contractor, what does that look like?',
    options: [
      { value: 'A', lead: 'Verbal-only.', desc: 'A rushed Slack/text or 5-minute call. They figure out the rest.' },
      { value: 'B', lead: 'Written brief.', desc: 'A short written brief plus me on standby for questions.' },
      { value: 'C', lead: 'SOP + form.', desc: 'A documented SOP plus a templated handoff form.' },
      { value: 'D', lead: 'System-driven.', desc: 'Structured intake → assigned in PM tool → SOP linked → automated check-ins.' },
    ] },
  { kind: 'radio', id: 'a8_knowledge', phase: 'pathA', label: 'Path A · 8 of 17',
    question: 'How is institutional knowledge — how-we-do-things, client preferences, vendor logins — captured?',
    options: [
      { value: 'A', lead: 'In my head.', desc: 'It mostly lives with me and a few people I trust.' },
      { value: 'B', lead: 'Scattered docs.', desc: 'Across Drive/Notion that may or may not be current.' },
      { value: 'C', lead: 'Central wiki.', desc: 'Mostly maintained but has stale corners.' },
      { value: 'D', lead: 'Living knowledge base.', desc: 'Ownership, review cadence, AI-searchable retrieval.' },
    ] },
  { kind: 'radio', id: 'a9_profitability', phase: 'pathA', label: 'Path A · 9 of 17',
    question: 'How do you know if you\'re profitable on a given client or project?',
    options: [
      { value: 'A', lead: 'Vibes.', desc: 'I look at the bank account and the P&L when the bookkeeper sends it.' },
      { value: 'B', lead: 'Spreadsheet at close.', desc: 'Manual margin calc at the end of a project.' },
      { value: 'C', lead: 'Tool, post-close.', desc: 'Per-project margin is tracked, but only after work is closed.' },
      { value: 'D', lead: 'Real-time dashboard.', desc: 'Live margin per client/project, including loaded labor cost.' },
    ] },
  { kind: 'radio', id: 'a10_ai_use', phase: 'pathA', label: 'Path A · 10 of 17',
    question: 'Which best describes how your team uses AI tools today?',
    options: [
      { value: 'A', lead: 'Just me, sometimes.', desc: 'I personally use ChatGPT occasionally; the team mostly doesn\'t.' },
      { value: 'B', lead: 'Ad hoc.', desc: 'A few people use AI tools they bought themselves; no shared playbook.' },
      { value: 'C', lead: 'Shared playbook.', desc: 'Shared accounts and a few documented prompts/workflows.' },
      { value: 'D', lead: 'Embedded.', desc: 'AI is built into specific business processes with measurable lift and governance.' },
    ] },
  { kind: 'radio', id: 'a11_forecasting', phase: 'pathA', label: 'Path A · 11 of 17',
    question: 'How do you forecast revenue or cash for the next 60–90 days?',
    options: [
      { value: 'A', lead: 'I don\'t.', desc: 'I don\'t, formally. I look at what\'s in the pipeline mentally.' },
      { value: 'B', lead: 'Spreadsheet, ad hoc.', desc: 'A spreadsheet I update before tax time or when a banker asks.' },
      { value: 'C', lead: 'Monthly model.', desc: 'A monthly forecast model with weighted pipeline data.' },
      { value: 'D', lead: 'Live forecast.', desc: 'Live cash and pipeline forecast updated automatically from our systems.' },
    ] },
  { kind: 'radio', id: 'a12_customer_list', phase: 'pathA', label: 'Path A · 12 of 17',
    question: 'What\'s your current relationship with your customer list?',
    options: [
      { value: 'A', lead: 'Inconsistent.', desc: 'I have one — somewhere — but I don\'t market to it consistently.' },
      { value: 'B', lead: 'Occasional.', desc: 'I send the occasional update or holiday email manually.' },
      { value: 'C', lead: 'Regular newsletter.', desc: 'Mostly batch sends from a marketing tool.' },
      { value: 'D', lead: 'Lifecycle marketing.', desc: 'Segmented, behavior-based, with measurable revenue contribution.' },
    ] },
  { kind: 'radio', id: 'a13_scalability', phase: 'pathA', label: 'Path A · 13 of 17',
    question: 'If your lead volume tripled tomorrow, what breaks first?',
    helper: 'Imagine the call comes in: marketing did its job. What\'s the bottleneck?',
    options: [
      { value: 'A', lead: 'People.', desc: 'I\'d have to hire admin/sales staff immediately to handle the data entry and follow-up.' },
      { value: 'B', lead: 'Process.', desc: 'Quality control would plummet because our handoffs are too manual.' },
      { value: 'C', lead: 'Capacity.', desc: 'We\'d struggle on fulfillment but the front-end would handle it.' },
      { value: 'D', lead: 'Nothing breaks.', desc: 'Our systems handle the heavy lifting; we\'d hire only for high-level service.' },
    ] },
  { kind: 'checkbox', id: 'a14_shadow_tools', phase: 'pathA', label: 'Path A · 14 of 17', multiSelect: true,
    question: 'Which of these "shadow tools" are currently load-bearing in your business?',
    helper: 'Check all that apply. If it disappeared tomorrow, would something break?',
    options: [
      { value: 'sheet_one_owner', text: 'A spreadsheet only one person knows how to update' },
      { value: 'gmail_imessage', text: 'A personal Gmail or text thread with critical client info' },
      { value: 'founder_phone', text: 'A founder\'s personal phone as the main inbound sales line' },
      { value: 'free_saas_tier', text: 'A free SaaS tier that would break the business if it disappeared' },
      { value: 'silent_spreadsheet', text: 'A spreadsheet that quietly runs operations no one else has touched' },
      { value: 'none', text: 'None of the above' },
    ] },
  { kind: 'checkbox', id: 'a15_personally_touched', phase: 'pathA', label: 'Path A · 15 of 17', multiSelect: true,
    question: 'Which of the following did you personally touch in the last 7 days?',
    helper: 'Check all that apply. Be honest — it\'s a snapshot of where your time goes.',
    options: [
      { value: 'lead_inquiry', text: 'Replying to an inbound lead inquiry' },
      { value: 'invoice', text: 'Manually creating or sending an invoice' },
      { value: 'quoting', text: 'Quoting or pricing new work' },
      { value: 'onboarding', text: 'Onboarding a new client (welcome info, setup)' },
      { value: 'small_ops_admin', text: 'Small ops admin (passwords, vendor chasing, copy/paste, spreadsheets)' },
      { value: 'none', text: 'None of the above' },
    ] },
  { kind: 'textarea', id: 'a16_two_weeks_text', phase: 'pathA', label: 'Path A · 16 of 17',
    question: 'If you had to disappear for two weeks — phone off, no contact — what falls over first, and who steps up?',
    helper: 'Specifics — names, processes, awkward dependencies. Messy is welcome.',
    placeholder: 'The first thing that would break is...' },
  { kind: 'textarea', id: 'a17_genie', phase: 'pathA', label: 'Path A · 17 of 17',
    question: 'A magic genie can automate, eliminate, or hand off three things from your week. What are they — and why haven\'t you fixed them yourself?',
    helper: 'Tells us where the energy is. Wild ideas welcome.',
    placeholder: 'The three things I\'d hand off are...' },

  // ── Path B ────────────────────────────────────────────────────────
  { kind: 'radio', id: 'b1_lead_journey', phase: 'pathB', label: 'Path B · 1 of 17',
    question: 'A qualified lead is captured by Marketing. Trace its journey to a closed-won, onboarded client. What does that handoff actually look like?',
    options: [
      { value: 'A', lead: 'Throw over the fence.', desc: 'Marketing emails or Slacks Sales. Sales updates the CRM later (sometimes). Ops finds out the client exists when the contract lands in their inbox.' },
      { value: 'B', lead: 'SLA, inconsistently followed.', desc: 'Documented SLA Marketing → Sales → Ops, but adherence is inconsistent and the Ops handoff is mostly manual.' },
      { value: 'C', lead: 'CRM-tracked, partial automation.', desc: 'Stage transitions tracked with required fields; partial automation hands off to Ops.' },
      { value: 'D', lead: 'System-to-system.', desc: 'Closed-won automatically provisions the customer in Ops, Finance, Success, and the data warehouse.' },
    ] },
  { kind: 'radio', id: 'b2_data_truth', phase: 'pathB', label: 'Path B · 2 of 17',
    question: 'Where does customer data live — really?',
    options: [
      { value: 'A', lead: 'Departmental silos.', desc: 'Sales has the CRM, Ops has its own system, Finance has its own, Support has its own — and none of them agree.' },
      { value: 'B', lead: 'Master CRM + shadow.', desc: 'A "master" CRM exists, but each department maintains private spreadsheets/docs that contradict it.' },
      { value: 'C', lead: 'Synced with gaps.', desc: 'Most systems sync via integrations or a CDP, with known gaps we work around.' },
      { value: 'D', lead: 'Governed platform.', desc: 'A governed warehouse/lakehouse is the single source of truth; downstream apps consume from it.' },
    ] },
  { kind: 'radio', id: 'b3_shadow_it', phase: 'pathB', label: 'Path B · 3 of 17',
    question: 'How prevalent is "Shadow IT" — tools individuals or teams adopted without IT or Ops knowing?',
    options: [
      { value: 'A', lead: 'No idea.', desc: 'And I suspect the answer is "a lot."' },
      { value: 'B', lead: 'Tolerated.', desc: 'We know about most of it but tolerate it because the team gets value.' },
      { value: 'C', lead: 'Audited 1–2x/year.', desc: 'We have a SaaS/tool register that\'s audited annually or semi-annually.' },
      { value: 'D', lead: 'Real-time discovery.', desc: 'SaaS discovery + procurement gating + named owners per tool.' },
    ] },
  { kind: 'radio', id: 'b4_approval_chain', phase: 'pathB', label: 'Path B · 4 of 17',
    question: 'Describe the approval chain to spend $5,000 on a new tool that would clearly save 20 hours/week across a team.',
    options: [
      { value: 'A', lead: 'Foggy.', desc: 'The request usually dies in someone\'s inbox or pings the CEO for a final yes.' },
      { value: 'B', lead: 'Documented but slow.', desc: 'A chain exists but it routinely takes weeks, and people work around it.' },
      { value: 'C', lead: 'Matrix-driven.', desc: 'Approval matrix with thresholds and named approvers — works but is email/Slack-driven.' },
      { value: 'D', lead: 'Procurement workflow.', desc: 'Auto-routing, vendor risk review, SLAs on each step.' },
    ] },
  { kind: 'radio', id: 'b5_ai_governance', phase: 'pathB', label: 'Path B · 5 of 17',
    question: 'How is AI usage currently governed across your organization?',
    helper: 'Are employees pasting proprietary code, financial data, or client PII into public AI models?',
    options: [
      { value: 'A', lead: 'It isn\'t.', desc: 'People paste data into ChatGPT and we hope for the best.' },
      { value: 'B', lead: 'Policy, no enforcement.', desc: 'A one-page "AI policy" but no enforcement and no logging.' },
      { value: 'C', lead: 'Approved tools list.', desc: 'Sanctioned accounts and basic DLP controls.' },
      { value: 'D', lead: 'Enterprise framework.', desc: 'Sanctioned platforms, prompt/response logging, role-based access, model risk framework.' },
    ] },
  { kind: 'checkbox', id: 'b6_reporting', phase: 'pathB', label: 'Path B · 6 of 17', multiSelect: true,
    question: 'Which of these are true about your reporting and KPIs?',
    helper: 'Check all that apply.',
    options: [
      { value: 'different_numbers', text: 'Different departments produce different numbers for the "same" metric' },
      { value: 'manual_wrangling', text: 'Monthly board reports require manual data wrangling' },
      { value: 'no_trust', text: 'No one fully trusts a particular dashboard or system' },
      { value: 'excel_decisions', text: 'We have BI tools, but most decisions come off Excel exports' },
      { value: 'slow_reporting', text: 'Reports take 2+ days to answer a leadership question' },
      { value: 'none', text: 'None of the above' },
    ] },
  { kind: 'radio', id: 'b7_key_person', phase: 'pathB', label: 'Path B · 7 of 17',
    question: 'How dependent are core processes on a single critical employee or vendor?',
    options: [
      { value: 'A', lead: 'Multiple risks.', desc: 'There are 2–3 people who, if they left tomorrow, would create operational crises.' },
      { value: 'B', lead: 'Identified, not solved.', desc: 'We\'ve identified key-person risks but haven\'t fully cross-trained or documented around them.' },
      { value: 'C', lead: 'Documented.', desc: 'Most critical knowledge is documented, with a backup person identified per process.' },
      { value: 'D', lead: 'Engineered resilience.', desc: 'Automation, documentation, and redundancy across systems and people.' },
    ] },
  { kind: 'radio', id: 'b8_legacy_systems', phase: 'pathB', label: 'Path B · 8 of 17',
    question: 'What\'s the state of your legacy or long-tenured systems — the ones nobody wants to touch?',
    options: [
      { value: 'A', lead: 'One person, custom scripts.', desc: 'At least one critical system held together by one person, custom scripts, or a vendor we resent.' },
      { value: 'B', lead: 'Known but unaddressed.', desc: 'We know what to replace but lack the time or political will to do it.' },
      { value: 'C', lead: 'Modernization in flight.', desc: 'Active roadmap with prioritized retirements.' },
      { value: 'D', lead: 'Modular, API-first.', desc: 'We can swap components without organizational drama.' },
    ] },
  { kind: 'radio', id: 'b9_customer_intel', phase: 'pathB', label: 'Path B · 9 of 17',
    question: 'How do customer-facing teams (Sales, CS, Support) share intelligence about a client?',
    options: [
      { value: 'A', lead: 'Verbal/DMs.', desc: 'Mostly verbally, in Slack DMs, or via the account owner. New people learn by asking.' },
      { value: 'B', lead: 'Inconsistent CRM notes.', desc: 'Notes in the CRM, but quality and completeness vary wildly by rep.' },
      { value: 'C', lead: 'Required activity templates.', desc: 'CRM activity templates, plus shared client briefs for handoffs.' },
      { value: 'D', lead: 'Unified profile.', desc: 'Auto-summarized interactions across channels with AI-generated account context.' },
    ] },
  { kind: 'radio', id: 'b10_manager_time', phase: 'pathB', label: 'Path B · 10 of 17',
    question: 'How are middle-managers spending their week, in your honest assessment?',
    options: [
      { value: 'A', lead: 'Status-chasing.', desc: 'A lot of status-chasing, manual reporting, and forwarding emails.' },
      { value: 'B', lead: 'Mixed.', desc: 'Some real management, plus admin/reporting overhead they shouldn\'t be doing.' },
      { value: 'C', lead: 'Coaching + unblocking.', desc: 'Mostly coaching, prioritization, and unblocking — admin is automated.' },
      { value: 'D', lead: 'Dashboard-driven.', desc: 'Live dashboards; status reports auto-generated and exception-based.' },
    ] },
  { kind: 'radio', id: 'b11_onboarding', phase: 'pathB', label: 'Path B · 11 of 17',
    question: 'When a new employee joins a customer-facing team, how long until they\'re truly productive?',
    options: [
      { value: 'A', lead: '6+ months.', desc: 'Lots of shoulder-tapping.' },
      { value: 'B', lead: '90 days.', desc: 'Tribal knowledge and a paper-thin runbook.' },
      { value: 'C', lead: '30–60 days.', desc: 'Structured onboarding curriculum and tools access.' },
      { value: 'D', lead: 'Under 30 days.', desc: 'Provisioned access, AI co-pilots, self-serve learning paths.' },
    ] },
  { kind: 'radio', id: 'b12_dept_handoff', phase: 'pathB', label: 'Path B · 12 of 17',
    question: 'How does work get handed across departments (Sales → Implementation, Ops → Finance)?',
    options: [
      { value: 'A', lead: 'Email + tribal.', desc: 'Things fall through cracks weekly.' },
      { value: 'B', lead: 'Tickets, varied quality.', desc: 'Forms exist, but content quality varies and rework is common.' },
      { value: 'C', lead: 'Standardized intake.', desc: 'Required data fields with some automated routing.' },
      { value: 'D', lead: 'System-to-system.', desc: 'Stage X completion automatically triggers stage Y with full context.' },
    ] },
  { kind: 'radio', id: 'b13_security', phase: 'pathB', label: 'Path B · 13 of 17',
    question: 'What is your security and access posture across SaaS tools?',
    options: [
      { value: 'A', lead: 'Shared logins.', desc: 'Reused passwords, ex-employees we think we\'ve offboarded.' },
      { value: 'B', lead: 'SSO, manual offboarding.', desc: 'Most tools use SSO; offboarding is a manual checklist.' },
      { value: 'C', lead: 'SSO + MFA enforced.', desc: 'Identity provider drives provisioning for major tools.' },
      { value: 'D', lead: 'IDP-driven lifecycle.', desc: 'SCIM, least-privilege roles, audit logs reviewed regularly.' },
    ] },
  { kind: 'radio', id: 'b14_tech_debt', phase: 'pathB', label: 'Path B · 14 of 17',
    question: 'How are you tracking and prioritizing technical debt and process debt?',
    options: [
      { value: 'A', lead: 'Reactively.', desc: 'It surfaces when something breaks.' },
      { value: 'B', lead: 'Engineering only.', desc: 'Engineering has a list; the business side doesn\'t.' },
      { value: 'C', lead: 'Quarterly review.', desc: 'A cross-functional debt register exists, reviewed quarterly.' },
      { value: 'D', lead: 'Portfolio item.', desc: 'Tech and process debt is a first-class portfolio item with allocated capacity.' },
    ] },
  { kind: 'checkbox', id: 'b15_uncomfortable_truths', phase: 'pathB', label: 'Path B · 15 of 17', multiSelect: true,
    question: 'Which of these statements ring uncomfortably true for your organization?',
    helper: 'Check all that apply.',
    options: [
      { value: 'unused_seats', text: 'We pay for software seats nobody uses' },
      { value: 'lost_deal', text: 'We\'ve lost a deal or renewal because of an internal handoff failure' },
      { value: 'painful_audit', text: 'An audit (security, financial, compliance) would be painful right now' },
      { value: 'tribal_process', text: 'A "tribal" process exists in one person\'s head and nowhere else' },
      { value: 'slow_data', text: 'A leadership question regularly takes days to answer with data' },
      { value: 'none', text: 'None of the above' },
    ] },
  { kind: 'textarea', id: 'b16_friction_text', phase: 'pathB', label: 'Path B · 16 of 17',
    question: 'Walk us through a recent cross-department handoff that went sideways. What was said vs. meant — and what did the customer experience?',
    helper: 'Name the departments, the handoff, the failure mode.',
    placeholder: 'A recent handoff that went sideways was...' },
  { kind: 'textarea', id: 'b17_blind_spots', phase: 'pathB', label: 'Path B · 17 of 17',
    question: 'What are you flying blind on?',
    helper: 'Questions about the business you want a real-time answer to — without Slacking a director on Friday night.',
    placeholder: 'I want a real-time answer to...' },

  // ── Convergence ───────────────────────────────────────────────────
  { kind: 'checkbox', id: 'c1_focus', phase: 'convergence', label: 'Convergence · 1 of 3', multiSelect: true, maxSelections: 2,
    question: 'Looking 12 to 18 months out, what are the top 1–2 things you\'re optimizing for?',
    helper: 'Pick at most 2. The roadmap weights toward your answer here above all others.',
    options: [
      { value: 'top_line', text: 'Aggressive top-line revenue growth' },
      { value: 'margins', text: 'Increasing profit margins and operational efficiency' },
      { value: 'founder_out', text: 'Removing the founder/owner from daily operations' },
      { value: 'data_unification', text: 'Unifying our data to make better executive decisions' },
      { value: 'exit_prep', text: 'Preparing the business for an exit, acquisition, or capital raise' },
      { value: 'time_back', text: 'Getting weekends/evenings back; quality of life' },
    ] },
  { kind: 'textarea', id: 'c2_winning', phase: 'convergence', label: 'Convergence · 2 of 3',
    question: 'What does "winning" look like 12–18 months from now?',
    helper: 'Be specific — revenue, headcount, getting out of a role, exit prep. The sharper your answer, the sharper the roadmap.',
    placeholder: 'In 12–18 months, winning looks like...' },
  { kind: 'textarea', id: 'c3_open_floor', phase: 'convergence', label: 'Convergence · 3 of 3', optional: true,
    question: 'What tech headache, recurring nightmare, pipe-dream automation, or weird operational quirk should we know about?',
    helper: 'No idea is too small or too ambitious. Optional, but highly encouraged.',
    placeholder: '(Optional) Something you want us to know is...' },
];

const QUESTION_BY_ID: Record<string, QuestionDef> = Object.fromEntries(QUESTIONS.map(q => [q.id, q]));

export function getQuestion(id: string): QuestionDef | undefined {
  return QUESTION_BY_ID[id];
}

/** Resolve an option value for a radio/checkbox question into its full
 * descriptor. Returns the original value verbatim when the question or
 * option isn't found, so unknown ids fall back to a readable string.
 */
export function getOption(qid: string, value: string): RadioOption | CheckboxOption | null {
  const q = QUESTION_BY_ID[qid];
  if (!q) return null;
  if (q.kind === 'radio') {
    return q.options.find(o => o.value === value) || null;
  }
  if (q.kind === 'checkbox') {
    return q.options.find(o => o.value === value) || null;
  }
  return null;
}

/** Order questions by their canonical phase + position in the array. */
export function questionsByPhase(): Record<Phase, QuestionDef[]> {
  const out: Record<Phase, QuestionDef[]> = {
    intake: [],
    qualifier: [],
    context: [],
    pathA: [],
    pathB: [],
    convergence: [],
  };
  for (const q of QUESTIONS) out[q.phase].push(q);
  return out;
}
